import condriLogo from "@/assets/condri-without-background.png"
import { ShieldCheck } from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAtomValue } from "jotai"
import { useAuth } from "@/auth/useAuth"
import {
  authErrorAtom,
  isAuthenticatedAtom,
  requiresTwoFactorAtom,
} from "@/auth/atoms"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"

/**
 * Two-factor verification for admin/staff login. After a magic link is verified
 * for an account with 2FA enabled, the backend returns a short-lived challenge
 * token (not an access token), so `requiresTwoFactor` is true. This page takes
 * the 6-digit TOTP code and exchanges the challenge for a real session, then
 * lands on the admin panel.
 */
const TwoFactorPage = () => {
  const navigate = useNavigate()
  const { verifyTwoFactor, clearError, isLoading } = useAuth()
  const requiresTwoFactor = useAtomValue(requiresTwoFactorAtom)
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const error = useAtomValue(authErrorAtom)
  const [code, setCode] = useState("")

  // Once the exchange succeeds an access token exists → head to the admin panel.
  // If there's no active 2FA challenge (and we're not signed in), there's nothing
  // to verify — send them back to the staff login.
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/admin", { replace: true })
    } else if (!requiresTwoFactor) {
      navigate("/auth/staff-login", { replace: true })
    }
  }, [isAuthenticated, requiresTwoFactor, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (code.length !== 6 || isLoading) return
    try {
      await verifyTwoFactor(code)
      // success → the effect above redirects once isAuthenticated flips
    } catch {
      // error surfaces via authErrorAtom; keep the user on the page to retry
      setCode("")
    }
  }

  return (
    <div className="min-h-screen max-w-md mx-auto bg-card flex flex-col">
      <div className="flex-1 flex flex-col justify-center px-6 pt-8">
        <div className="text-center space-y-6">
          <div className="mx-auto w-20 h-20 bg-primary-faded rounded-full flex items-center justify-center p-2">
            <img
              src={condriLogo}
              alt="Condri Logo"
              className="w-full h-full object-contain"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2">
              <ShieldCheck className="w-6 h-6 text-primary" />
              <h1 className="text-3xl font-bold text-foreground">
                Two-factor code
              </h1>
            </div>
            <p className="text-muted-foreground text-lg">
              Enter the 6-digit code from your authenticator app to finish
              signing in.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col px-6 pb-10 space-y-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="000000"
            aria-label="Two-factor authentication code"
            value={code}
            onChange={(e) => {
              const next = e.target.value.replace(/\D/g, "").slice(0, 6)
              setCode(next)
              if (error) clearError()
            }}
            className="text-center text-2xl tracking-[0.5em] font-mono h-14"
          />

          {error && (
            <p role="alert" className="text-sm text-destructive text-center">
              {error.type === "invalid_totp"
                ? "That code didn't match. Check your authenticator app and try again."
                : error.message || "Verification failed. Please try again."}
            </p>
          )}

          <Button
            type="submit"
            className="w-full h-12 text-base"
            disabled={code.length !== 6 || isLoading}
          >
            {isLoading ? "Verifying…" : "Verify"}
          </Button>
        </form>

        <button
          type="button"
          onClick={() => navigate("/auth/staff-login", { replace: true })}
          className="text-sm text-muted-foreground hover:text-foreground text-center"
        >
          Back to login
        </button>
      </div>
    </div>
  )
}

export default TwoFactorPage
